/**
 * Tracking and copy for attachment uploads that are still in flight at send time. An attachment is
 * pending from the moment its file starts uploading until `addAttachmentToInboxDraft` lands — until
 * then it is not on the draft server-side, so anything sent in that window goes out without it.
 */

export interface PendingAttachmentUploadsOptions {
  /** Attachment uploads that have started but are not yet persisted to the draft. */
  pendingAttachmentUploadCount?: number;
}

export interface PendingAttachmentUploadRegistry {
  /** Records one upload as in flight for `draftId`. Call the returned release once it settles. */
  begin: (draftId: string) => () => void;
  /** How many uploads have started for `draftId` but are not yet persisted to it. */
  getCount: (draftId: string) => number;
}

/**
 * Tracks in-flight uploads per draft id, outside any one draft editor.
 *
 * Switching a draft between the inline and floating trees unmounts its `DraftProvider` and mounts a
 * fresh one, so anything a single editor holds is lost across the swap while its uploads keep
 * running — leaving the new editor's send confirmation silent about them. Owning this above the
 * editor is what survives the swap. Nothing renders from it, so it is deliberately not React state.
 */
export const createPendingAttachmentUploadRegistry = (): PendingAttachmentUploadRegistry => {
  // Tokens rather than a counter, so releasing is idempotent: a double release cannot drive a
  // draft's count below the number of uploads actually running.
  const inFlightByDraftId = new Map<string, Set<symbol>>();

  return {
    begin: (draftId) => {
      const token = Symbol("pendingAttachmentUpload");
      const inFlight = inFlightByDraftId.get(draftId) ?? new Set<symbol>();

      inFlight.add(token);
      inFlightByDraftId.set(draftId, inFlight);

      return () => {
        const remaining = inFlightByDraftId.get(draftId);

        if (!remaining) {
          return;
        }

        remaining.delete(token);

        // Drop the key rather than leave an empty set behind for every draft ever composed.
        if (remaining.size === 0) {
          inFlightByDraftId.delete(draftId);
        }
      };
    },
    getCount: (draftId) => inFlightByDraftId.get(draftId)?.size ?? 0,
  };
};

export const pendingAttachmentUploadsSendWarning = (count: number): string =>
  count === 1
    ? "1 attachment is still uploading. Sending before it finishes will send the draft without it."
    : `${count} attachments are still uploading. Sending before they finish will send the draft without them.`;

export const pendingAttachmentUploadsScheduleWarning = (count: number): string =>
  count === 1
    ? "1 attachment is still uploading. If it doesn't finish before the scheduled time, the draft will be sent without it."
    : `${count} attachments are still uploading. If they don't finish before the scheduled time, the draft will be sent without them.`;
